import React, { useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import { formatDateTime } from '../../../config/appConfig';
import { Button } from '../../../components/ui';
import { EyeIcon, InfoIcon, RefreshIcon } from '../../../components/ui/Icons';
import { useTheme, type ThemeColors } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import {
  cancelOutboxApi,
  dismissOutboxApi,
  resendOutboxApi,
  useOutboxList,
  type EmailOutboxListItem,
  type EmailOutboxStatus,
} from '../useData';
import type { ComposeReturnTarget } from '../utils/composeNavigation';
import OutboxDetailModal from './OutboxDetailModal';
import { OutboxSendSchedule } from './OutboxSendSchedule';
import { outboxQueuedSuccessMessage } from '../utils/outboxMessages';

function formatAddressList(addrs?: string[]): string {
  return (addrs ?? []).filter(Boolean).join(', ');
}

function statusLabel(status: EmailOutboxStatus): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'sending':
      return 'Sending';
    case 'failed':
      return 'Failed';
    case 'sent':
      return 'Sent';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
}

function statusBadgeClass(status: EmailOutboxStatus, colors: ThemeColors): string {
  switch (status) {
    case 'queued':
      return cn('bg-blue-500/15 text-blue-700 dark:text-blue-300');
    case 'sending':
      return cn('bg-amber-500/15 text-amber-700 dark:text-amber-300');
    case 'failed':
      return cn('bg-red-500/15 text-red-700 dark:text-red-300');
    case 'sent':
      return cn('bg-green-500/15 text-green-700 dark:text-green-300');
    case 'cancelled':
      return cn('bg-slate-500/15 text-slate-600 dark:text-slate-400');
    default:
      return cn(colors.surfaceHover, colors.textSecondary);
  }
}

function outboxFilterBadgeClass(filter: OutboxListFilter, active: boolean, colors: ThemeColors): string {
  if (filter === 'all') {
    return active
      ? cn(colors.primary, colors.primaryText, 'border-transparent shadow-sm')
      : cn(colors.surface, colors.border, colors.textMuted, colors.interactiveHover);
  }

  switch (filter) {
    case 'queued':
      return active
        ? 'bg-blue-600 text-white border-blue-600 shadow-sm dark:bg-blue-500 dark:border-blue-500'
        : 'bg-blue-500/15 text-blue-700 border-blue-500/35 dark:text-blue-300 hover:bg-blue-500/25';
    case 'sending':
      return active
        ? 'bg-amber-600 text-white border-amber-600 shadow-sm dark:bg-amber-500 dark:border-amber-500'
        : 'bg-amber-500/15 text-amber-700 border-amber-500/35 dark:text-amber-300 hover:bg-amber-500/25';
    case 'failed':
      return active
        ? 'bg-red-600 text-white border-red-600 shadow-sm dark:bg-red-500 dark:border-red-500'
        : 'bg-red-500/15 text-red-700 border-red-500/35 dark:text-red-300 hover:bg-red-500/25';
    case 'cancelled':
      return active
        ? 'bg-slate-600 text-white border-slate-600 shadow-sm dark:bg-slate-500 dark:border-slate-500'
        : 'bg-slate-500/15 text-slate-600 border-slate-500/35 dark:text-slate-400 hover:bg-slate-500/25';
    default:
      return cn(colors.surface, colors.border, colors.textMuted, colors.interactiveHover);
  }
}

function outboxFilterCountBadgeClass(filter: OutboxListFilter, active: boolean, colors: ThemeColors): string {
  if (filter === 'all') {
    return active ? 'bg-black/15 dark:bg-white/15' : cn(colors.surfaceHover, colors.textSecondary);
  }

  switch (filter) {
    case 'queued':
      return active ? 'bg-white/25 text-white' : 'bg-blue-500/20 text-blue-800 dark:text-blue-200';
    case 'sending':
      return active ? 'bg-white/25 text-white' : 'bg-amber-500/20 text-amber-800 dark:text-amber-200';
    case 'failed':
      return active ? 'bg-white/25 text-white' : 'bg-red-500/20 text-red-800 dark:text-red-200';
    case 'cancelled':
      return active ? 'bg-white/25 text-white' : 'bg-slate-500/20 text-slate-700 dark:text-slate-300';
    default:
      return cn(colors.surfaceHover, colors.textSecondary);
  }
}

interface OutboxListProps {
  composeReturnTo: ComposeReturnTarget;
}

type OutboxListFilter = 'all' | EmailOutboxStatus;

const OUTBOX_STATUS_FILTERS: EmailOutboxStatus[] = ['queued', 'sending', 'failed', 'cancelled'];

const OUTBOX_STATUS_LEGEND: { status: EmailOutboxStatus; meaning: string }[] = [
  {
    status: 'queued',
    meaning: 'In the outbox, waiting for the undo delay or scheduledSendAt. User can Cancel send.',
  },
  {
    status: 'sending',
    meaning: 'The background worker picked up the job and is calling SMTP. Too late to cancel.',
  },
  {
    status: 'sent',
    meaning: 'Delivered successfully (removed from the outbox list).',
  },
  {
    status: 'failed',
    meaning: 'SMTP or server error.',
  },
  {
    status: 'cancelled',
    meaning: 'User cancelled while still queued.',
  },
];

const OUTBOX_FILTER_OPTIONS: { id: OutboxListFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  ...OUTBOX_STATUS_FILTERS.map((status) => ({ id: status, label: statusLabel(status) })),
];

function outboxFilterCount(items: EmailOutboxListItem[], filter: OutboxListFilter): number {
  if (filter === 'all') return items.length;
  return items.filter((item) => item.status === filter).length;
}

function isOutboxStatusFilterActive(
  filter: OutboxListFilter,
  selectedStatuses: Set<EmailOutboxStatus> | 'all',
): boolean {
  if (filter === 'all') return selectedStatuses === 'all';
  return selectedStatuses === 'all' ? false : selectedStatuses.has(filter);
}

function toggleOutboxStatusFilter(
  filter: OutboxListFilter,
  selectedStatuses: Set<EmailOutboxStatus> | 'all',
): Set<EmailOutboxStatus> | 'all' {
  if (filter === 'all') return 'all';

  if (selectedStatuses === 'all') {
    return new Set([filter]);
  }

  const next = new Set(selectedStatuses);
  if (next.has(filter)) {
    next.delete(filter);
    return next.size === 0 ? 'all' : next;
  }

  next.add(filter);
  return OUTBOX_STATUS_FILTERS.every((status) => next.has(status)) ? 'all' : next;
}

function filterOutboxItems(
  items: EmailOutboxListItem[],
  selectedStatuses: Set<EmailOutboxStatus> | 'all',
): EmailOutboxListItem[] {
  if (selectedStatuses === 'all') return items;
  return items.filter((item) => selectedStatuses.has(item.status));
}

function outboxFilterEmptyMessage(selectedStatuses: Set<EmailOutboxStatus> | 'all'): string {
  if (selectedStatuses === 'all') return 'No messages match this filter.';

  const labels = OUTBOX_STATUS_FILTERS.filter((status) => selectedStatuses.has(status)).map((status) =>
    statusLabel(status).toLowerCase(),
  );
  if (labels.length === 1) return `No ${labels[0]} messages match this filter.`;
  return `No messages match the selected filters (${labels.join(', ')}).`;
}

function OutboxStatusLegendInfo({ colors }: { colors: ThemeColors }) {
  return (
    <div className="relative shrink-0 group/legend ml-auto">
      <button
        type="button"
        className={cn(
          'inline-flex items-center justify-center rounded-full p-1 transition-colors',
          colors.textMuted,
          colors.interactiveHover,
        )}
        aria-label="Outbox status meanings"
        aria-describedby="outbox-status-legend"
      >
        <InfoIcon className="w-4 h-4" />
      </button>
      <div
        id="outbox-status-legend"
        role="tooltip"
        className={cn(
          'absolute right-0 top-full z-50 pt-1.5',
          'opacity-0 invisible pointer-events-none',
          'group-hover/legend:opacity-100 group-hover/legend:visible',
          'group-focus-within/legend:opacity-100 group-focus-within/legend:visible',
        )}
      >
        <div className={cn('w-72 sm:w-[22rem] rounded-lg border p-3 shadow-lg', colors.surface, colors.border)}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-wide pb-1.5 pr-2 w-24',
                    colors.textMuted,
                  )}
                >
                  Status
                </th>
                <th className={cn('text-[10px] font-semibold uppercase tracking-wide pb-1.5', colors.textMuted)}>
                  Meaning
                </th>
              </tr>
            </thead>
            <tbody>
              {OUTBOX_STATUS_LEGEND.map((row) => (
                <tr key={row.status} className="align-top">
                  <td className="py-1.5 pr-2">
                    <span
                      className={cn(
                        'inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded',
                        statusBadgeClass(row.status, colors),
                      )}
                    >
                      {statusLabel(row.status)}
                    </span>
                  </td>
                  <td className={cn('py-1.5 text-xs leading-snug', colors.textSecondary)}>{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const OutboxList: React.FC<OutboxListProps> = ({ composeReturnTo }) => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { data: items, isLoading, isError, mutate } = useOutboxList();
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [dismissingId, setDismissingId] = useState<number | null>(null);
  const [openOutboxId, setOpenOutboxId] = useState<number | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<EmailOutboxStatus> | 'all'>('all');

  const filteredItems = useMemo(
    () => filterOutboxItems(items, selectedStatuses),
    [items, selectedStatuses],
  );

  const actionErrorMessage = (err: unknown, fallback: string): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      return (err as { response?: { data?: { message?: string } } }).response?.data?.message || fallback;
    }
    return fallback;
  };

  const handleCancel = async (id: number) => {
    setCancellingId(id);
    try {
      await cancelOutboxApi(id);
      await mutate();
      showSuccess('Send cancelled');
    } catch (err: unknown) {
      showError(actionErrorMessage(err, 'Failed to cancel send'));
      await mutate();
    } finally {
      setCancellingId(null);
    }
  };

  const handleResend = async (id: number) => {
    setResendingId(id);
    try {
      const result = await resendOutboxApi(id);
      await mutate();
      showSuccess(outboxQueuedSuccessMessage(result.undoDelaySeconds ?? 0));
    } catch (err: unknown) {
      showError(actionErrorMessage(err, 'Failed to resend message'));
      await mutate();
    } finally {
      setResendingId(null);
    }
  };

  const handleDismiss = async (id: number) => {
    setDismissingId(id);
    try {
      await dismissOutboxApi(id);
      await mutate();
      showSuccess('Removed from outbox');
    } catch (err: unknown) {
      showError(actionErrorMessage(err, 'Failed to remove item'));
      await mutate();
    } finally {
      setDismissingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className={colors.textMuted}>Loading outbox...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className={colors.textMuted}>Failed to load outbox.</span>
      </div>
    );
  }

  const filterBar = (
    <div
      className={cn(
        'flex-shrink-0 flex flex-wrap items-center gap-1.5 px-3 py-2 border-b',
        colors.border,
        colors.headerBg,
      )}
      role="toolbar"
      aria-label="Outbox filters"
    >
      <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
        {OUTBOX_FILTER_OPTIONS.map((option) => {
          const count = outboxFilterCount(items, option.id);
          const active = isOutboxStatusFilterActive(option.id, selectedStatuses);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setSelectedStatuses((prev) => toggleOutboxStatusFilter(option.id, prev))}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                outboxFilterBadgeClass(option.id, active, colors),
              )}
              aria-pressed={active}
            >
              <span>{option.label}</span>
              <span
                className={cn(
                  'tabular-nums rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                  outboxFilterCountBadgeClass(option.id, active, colors),
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
      <OutboxStatusLegendInfo colors={colors} />
    </div>
  );

  if (!items.length) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {filterBar}
        <div className="flex-1 flex items-center justify-center">
          <div className={cn('text-center', colors.textMuted)}>
            <p className="text-lg mb-1">Outbox is empty</p>
            <p className="text-sm">Queued, failed, and cancelled sends appear here.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 flex flex-col min-h-0">
        {filterBar}
        {filteredItems.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <p className={cn('text-sm text-center', colors.textMuted)}>
              {outboxFilterEmptyMessage(selectedStatuses)}
            </p>
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {filteredItems.map((item: EmailOutboxListItem) => {
            const accountLabel =
              item.emailAccount?.displayName ||
              item.emailAccount?.emailAddress ||
              `Account #${item.emailAccountId}`;
            const toDisplay = formatAddressList(item.recipients?.to);
            const created = item.createdAt ? new Date(item.createdAt) : null;
            const canCancel = item.status === 'queued';
            const canResend = item.status === 'failed';
            const canDismiss = item.status === 'failed';
            const actionBusy =
              cancellingId === item.id || resendingId === item.id || dismissingId === item.id;

            return (
              <article
                key={item.id}
                className={cn(
                  'rounded-lg border overflow-hidden transition-shadow hover:shadow-sm',
                  colors.surface,
                  colors.border,
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpenOutboxId(item.id)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 transition-colors',
                    colors.interactiveHover,
                  )}
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
                        {accountLabel}
                      </span>
                      <span
                        className={cn(
                          'text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded',
                          statusBadgeClass(item.status, colors),
                        )}
                      >
                        {statusLabel(item.status)}
                      </span>
                    </div>
                    {created && (
                      <span className={cn('text-xs shrink-0', colors.textMuted)}>{formatDateTime(item.createdAt)}</span>
                    )}
                  </div>
                  <div className={cn('text-sm font-medium line-clamp-2', colors.text)}>
                    {item.subject || '(no subject)'}
                  </div>
                  {toDisplay && (
                    <div className={cn('text-xs truncate mt-1', colors.textMuted)}>To: {toDisplay}</div>
                  )}
                  {(item.status === 'queued' || item.status === 'sending') && (
                    <OutboxSendSchedule
                      scheduledSendAt={item.scheduledSendAt}
                      status={item.status}
                      variant="card"
                    />
                  )}
                  {item.status === 'cancelled' && (
                    <div className={cn('text-xs mt-2', colors.textMuted)}>
                      Send cancelled before delivery
                      {item.updatedAt && (
                        <span> · {formatDateTime(item.updatedAt)}</span>
                      )}
                    </div>
                  )}
                </button>

                {item.errorMessage && (
                  <div className="mx-3 mb-2 rounded-md border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 px-2.5 py-2">
                    <p className="text-xs text-red-700 dark:text-red-300 line-clamp-3 break-words">
                      {item.errorMessage}
                    </p>
                  </div>
                )}

                <div
                  className={cn(
                    'flex flex-wrap items-center justify-end gap-2 px-3 py-2 border-t',
                    colors.border,
                    colors.surfaceHover,
                  )}
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenOutboxId(item.id)}
                  >
                    <EyeIcon className="w-4 h-4" />
                    Open
                  </Button>
                  {canCancel && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={actionBusy}
                      onClick={() => handleCancel(item.id)}
                    >
                      {cancellingId === item.id ? 'Cancelling…' : 'Cancel send'}
                    </Button>
                  )}
                  {canResend && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={actionBusy}
                      loading={resendingId === item.id}
                      onClick={() => handleResend(item.id)}
                    >
                      <RefreshIcon className="w-4 h-4" />
                      Resend
                    </Button>
                  )}
                  {canDismiss && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={actionBusy}
                      onClick={() => handleDismiss(item.id)}
                    >
                      {dismissingId === item.id ? 'Removing…' : 'Remove'}
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        )}
      </div>

      <OutboxDetailModal
        outboxId={openOutboxId}
        isOpen={openOutboxId != null}
        onClose={() => setOpenOutboxId(null)}
        composeReturnTo={composeReturnTo}
        onChanged={() => void mutate()}
      />
    </>
  );
};

export default OutboxList;
